import React from "react";
import { Spin } from "antd";
import dayjs from "dayjs";
import { fieldMap } from "@/config/dataMap";
import { rebuildFormula } from "@/config/func";
import { useCompareData } from "../ResultPageFrame";
import { useAppContext } from "@/AppContext";
import "./../../index.less"; // 参数面板/对比表格等全局样式
import "./ElementParamCompare.less"; // 参数网格等局部样式

interface ElementParamCompareProps {
  element?: any; // 当前元件的输入参数（input.inputs.elements 中的一项）
  stream?: any; // 当前流股的输入参数（可选）
  materials?: any[]; // 全组分列表（可选，缺省用流程 global_data.materials）
}

// 输入参数的元件/流股展示 + 对比
// 从 TaskParams/taskIndex 提取，供单元操作塔/流股结果页复用
const ElementParamCompare = ({ element, stream, materials: materialsProp = [] }: ElementParamCompareProps) => {
  const { compareItems } = useCompareData();
  // 流程全局组分列表（与 buildInput 中 spec 索引的来源 global_data.materials 一致）
  const { state } = useAppContext();
  const globalMaterials = state?.curFlow?.global_data?.materials || [];
  const materials = materialsProp.length > 0 ? materialsProp : globalMaterials;

  const currentEl = element || null;
  const currentSt = stream || null;
  const isCompareMode = compareItems.length > 0;

  // 优先用各页面统一上报的完整输入数据（fullInput），否则回退 mix_input
  const compareElementList = compareItems.map(
    (item: any) =>
      item?.values?.fullInput?.elements?.find((e: any) => e.name === currentEl?.name) ||
      item?.values?.mix_input?.elements?.find((e: any) => e.name === currentEl?.name) ||
      null,
  );

  const compareStreamList = compareItems.map(
    (item: any) =>
      item?.values?.fullInput?.streams?.find((s: any) => s.name === currentSt?.name) ||
      item?.values?.mix_input?.streams?.find((s: any) => s.name === currentSt?.name) ||
      null,
  );

  // 表格列构建
  const tableColumns = [
    { title: "参数名称", key: "label" },
    { title: "当前值", key: "current" },
    ...compareItems.map((item: any, i: number) => ({
      title: `${item.type === "task" ? item.values.name
        : item.type === "otherCalc"
          ? `${item.values.name} (${item.created_at ? dayjs(item.created_at).add(8, "hours").format("YYYY-MM-DD HH:mm:ss") : ''})`
          : item.type === "design" ? `设计值 (${item.id})` : (item.name || item.values?.name) || (item.type === "timeRange" ? (item.fullDay ? `运行数据 ${item.date}` : `运行数据 ${item.startTime} ~ ${item.endTime}`) : `运行数据 (${item.id})`)}`,
      key: `compare_${i}`,
      type: item.type,
    })),
  ];

  // 判断当前值与对比值是否有差异（用于整行标红）
  const valuesDiffer = (current: any, compare: any): boolean => {
    // 任一侧缺失（页面显示为 "-"）都不算差异，不标红
    const curMissing = current === "-" || current === null || current === undefined;
    const cmpMissing = compare === "-" || compare === null || compare === undefined;
    if (curMissing || cmpMissing) return false;
    const curStr = String(current ?? "");
    const cmpStr = String(compare ?? "");
    const curNum = parseFloat(curStr);
    const cmpNum = parseFloat(cmpStr);
    const curIsNum = !isNaN(curNum);
    const cmpIsNum = !isNaN(cmpNum);
    if (curIsNum && cmpIsNum) {
      // 数值比较（parseFloat 会忽略单位与误差百分比后缀）：相对容差比较，忽略浮点噪声（相对误差 < 0.0000001% 视为相等）
      if (curNum === cmpNum) return false;
      const tolerance = Math.max(Math.abs(curNum), Math.abs(cmpNum)) * 1e-9;
      return Math.abs(curNum - cmpNum) > tolerance;
    }
    // 非数值：字符串直接比较
    return curStr !== cmpStr;
  };

  // 生成设备参数表格行
  const getElementTableRows = () => {
    if (!currentEl) return [];
    const rows: any[] = [];
    const cfg: any = fieldMap.elements;
    const data = { ...currentEl, ...currentEl.params };

    Object.entries(cfg).forEach(([key, c]: any) => {
      if (data[key] === undefined || data[key] === null) return;
      const val = c.enum?.[data[key]] ?? data[key];
      const unit = data[c.unitKey] ?? c.unit ?? "";

      rows.push({
        label: c.name,
        current: `${val} ${unit}`,
        compares: compareElementList.map((cel: any) => {
          if (!cel) return "-";
          const d = { ...cel, ...cel.params };
          const v = c.enum?.[d[key]] ?? d[key];
          if (v === undefined || v === null) return "-";
          return `${v} ${unit}`;
        }),
      });
    });

    // 进料位置
    if (currentEl.params.Ns && currentEl.inputStreams) {
      currentEl.inputStreams.forEach((s: any, i: number) => {
        rows.push({
          label: `${s.name} 进料位置`,
          current: currentEl.params.Ns[i] ?? "-",
          compares: compareElementList.map((cel: any) => cel?.params?.Ns?.[i] ?? "-"),
        });
      });
    }

    // 出料位置
    if (currentEl.params.No && currentEl.outputStreams) {
      currentEl.outputStreams.forEach((s: any, i: number) => {
        rows.push({
          label: `${s.name} 出料位置`,
          current: currentEl.params.No[i] ?? "-",
          compares: compareElementList.map((cel: any) => cel?.params?.No?.[i] ?? "-"),
        });
      });
    }

    // 入口流股 / 出口流股（换热器显示冷热属性，其他元件仅列名称；塔除外）
    if (currentEl.type === "ShellAndTubeExchanger") {
      const streamValue = (s: any) => s?.streamAttribute || "—";
      if (currentEl.inputStreams?.length > 0) {
        currentEl.inputStreams.forEach((s: any) => {
          rows.push({
            label: `入口流股 - ${s.name}`,
            current: streamValue(s),
            compares: compareElementList.map((cel: any) => {
              if (!cel) return "-";
              const cs = cel.inputStreams?.find((x: any) => x.name === s.name);
              return cs ? streamValue(cs) : "-";
            }),
          });
        });
      }
      if (currentEl.outputStreams?.length > 0) {
        currentEl.outputStreams.forEach((s: any) => {
          rows.push({
            label: `出口流股 - ${s.name}`,
            current: streamValue(s),
            compares: compareElementList.map((cel: any) => {
              if (!cel) return "-";
              const cs = cel.outputStreams?.find((x: any) => x.name === s.name);
              return cs ? streamValue(cs) : "-";
            }),
          });
        });
      }
    }

    // 输出流量
    if (currentEl.params?.output_f && currentEl.outputStreams?.length > 0) {
      currentEl.outputStreams.forEach((s: any, idx: number) => {
        if (currentEl.params.output_f[idx] === undefined) return;
        rows.push({
          label: `输出流量 - ${s.name}`,
          current: `${currentEl.params.output_f[idx]}${currentEl.params.f_unit ? ` ${currentEl.params.f_unit}` : ""}`,
          compares: compareElementList.map((cel: any) => {
            if (!cel) return "-";
            const v = cel?.params?.output_f?.[idx];
            if (v === undefined) return "-";
            return `${v}${cel.params?.f_unit ? ` ${cel.params.f_unit}` : ""}`;
          }),
        });
      });
    }

    // 支持任意多组 塔板效率
    if (currentEl.params.eff && currentEl.params.eff.length > 0) {
      currentEl.params.eff.forEach((eff: any, idx: number) => {
        const prefix = idx + 1;
        rows.push(
          {
            label: `塔板效率 ${prefix} - 起始塔板`,
            current: eff.start ?? "-",
            compares: compareElementList.map((c: any) => c?.params?.eff?.[idx]?.start ?? "-"),
          },
          {
            label: `塔板效率 ${prefix} - 结束塔板`,
            current: eff.end ?? "-",
            compares: compareElementList.map((c: any) => c?.params?.eff?.[idx]?.end ?? "-"),
          },
          {
            label: `塔板效率 ${prefix} - 效率值`,
            current: eff.value ?? "-",
            compares: compareElementList.map((c: any) => c?.params?.eff?.[idx]?.value ?? "-"),
          },
        );

        // 特殊组分效率对比：spec 为 [[组分索引(1-based), 效率值], ...]
        if (Array.isArray(eff.spec) && eff.spec.length > 0) {
          eff.spec.forEach((sp: any, si: number) => {
            const mat = materials?.[Number(sp?.[0]) - 1];
            const matName = mat?.name || mat?.Name_ZH || mat?.chineseName || mat?.Molecular_formula || `组分${sp?.[0]}`;
            rows.push({
              label: `塔板效率 ${prefix} - 特殊效率（${matName}）`,
              current: sp?.[1] ?? "-",
              compares: compareElementList.map((c: any) => c?.params?.eff?.[idx]?.spec?.[si]?.[1] ?? "-"),
            });
          });
        }
      });
    }

    return rows;
  };

  // 生成流股参数表格行
  const getStreamTableRows = () => {
    if (!currentSt) return [];
    const rows: any[] = [];
    const cfg: any = fieldMap.streams;
    const data = currentSt.params;

    Object.entries(cfg).forEach(([key, c]: any) => {
      if (data[key] === undefined || data[key] === null) return;
      const val = c.enum?.[data[key]] ?? data[key];
      const unit = data[c.unitKey] ?? c.unit ?? "";
      rows.push({
        label: c.name,
        current: `${val} ${unit}`,
        compares: compareStreamList.map((cst: any) => {
          if (!cst) return "-";
          const v = c.enum?.[cst.params[key]] ?? cst.params[key];
          if (v === undefined) return "-";
          return `${v} ${unit}`;
        }),
      });
    });

    // 组分
    if (currentSt.materials?.length) {
      currentSt.materials.forEach((mat: any, i: number) => {
        const name = mat.cas === "7732-18-5" ? "水" : mat.cas === "67-56-1" ? "甲醇" : mat.name;
        rows.push({
          label: `${rebuildFormula(name)} 摩尔分率`,
          current: mat.molar_frac,
          compares: compareStreamList.map((cst: any) => {
            const compareVal = cst?.materials?.[i]?.molar_frac;
            if (compareVal === undefined || compareVal === null) return "-";
            return `${compareVal}`;
          }),
        });
      });
    }

    return rows;
  };

  // 原始布局渲染函数（无对比时使用）
  const renderParamRows = (data: any, type: "elements" | "streams") => {
    if (!data) return null;
    const fieldConfig: any = fieldMap[type];
    let params: any = type === "streams" ? data.params : { ...data, ...data.params };

    return Object.entries(fieldConfig).map(([key, config]: any) => {
      if (params[key] === undefined || params[key] === null) return null;
      let value = config.enum?.[params[key]] ?? params[key];
      const unit = params[config.unitKey] ?? config.unit;
      return (
        <div key={key} className="paramRow">
          <span className="paramLabel">{config.name}</span>
          <span className="paramValue">
            {value} {unit && <span className="paramUnit">{unit}</span>}
          </span>
        </div>
      );
    });
  };

  const renderComponentRows = (materials: any[]) => {
    if (!materials || materials.length === 0) return null;
    return materials.map((mat: any) => (
      <div key={mat.cas} className="paramRow">
        <span className="paramLabel">
          {mat.cas === "7732-18-5"
            ? "水"
            : mat.cas === "67-56-1"
              ? "甲醇"
              : rebuildFormula(mat.name)}
        </span>
        <span className="paramValue">{mat.molar_frac}</span>
      </div>
    ));
  };

  const renderFeedPositions = (Ns: number[], inputStreams: any[]) => {
    if (!Ns || !inputStreams || Ns.length !== inputStreams.length) return null;
    return inputStreams.map((stream: any, idx: number) => (
      <div key={stream.name} className="paramRow">
        <span className="paramLabel">{`${stream.name} 进料位置`}</span>
        <span className="paramValue">{Ns[idx]}</span>
      </div>
    ));
  };

  const renderProductPositions = (No: number[], outputStreams: any[]) => {
    if (!No || !outputStreams || No.length !== outputStreams.length) return null;
    return outputStreams.map((stream: any, idx: number) => (
      <div key={stream.name} className="paramRow">
        <span className="paramLabel">{`${stream.name} 出料位置`}</span>
        <span className="paramValue">{No[idx]}</span>
      </div>
    ));
  };

  const elementRows = getElementTableRows();
  const streamRows = getStreamTableRows();

  if (!currentEl && !currentSt) return null;

  return (
    <div className="parampart">
      {isCompareMode ? (
        // 有对比 → 表格展示
        currentEl ? (
          <div className="paramContent">
            {/* <h2 className="paramTitle">{currentEl.name} - 设备参数对比</h2> */}
            <h4 className="taskParamTitle">输入参数对比</h4>
            <table className="compare-table">
              <thead>
                <tr>
                  {tableColumns.map((col) => (
                    <th key={col.key}>{col.title}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {elementRows.map((row, i) => {
                  const rowHasDiff = row.compares.some((v: any) => valuesDiffer(row.current, v));
                  return (
                    <tr key={i} className={rowHasDiff ? "compare-row-diff" : ""}>
                      <td>{row.label}</td>
                      <td className="compare-value-current">{row.current}</td>
                      {row.compares.map((v, j) => (
                        <td
                          key={j}
                          className={
                            compareItems[j]?.type === "design"
                              ? "compare-value-design"
                              : "compare-value-actual"
                          }
                        >
                          {v}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : currentSt ? (
          <div className="paramContent">
            <h2 className="paramTitle">{currentSt.name} - 流股参数对比</h2>
            <table className="compare-table">
              <thead>
                <tr>
                  {tableColumns.map((col) => (
                    <th key={col.key}>{col.title}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {streamRows.map((row, i) => {
                  const rowHasDiff = row.compares.some((v: any) => valuesDiffer(row.current, v));
                  return (
                    <tr key={i} className={rowHasDiff ? "compare-row-diff" : ""}>
                      <td>{row.label}</td>
                      <td className="compare-value-current">{row.current}</td>
                      {row.compares.map((v, j) => (
                        <td
                          key={j}
                          className={
                            compareItems[j]?.type === "design"
                              ? "compare-value-design"
                              : "compare-value-actual"
                          }
                        >
                          {v}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null
      ) : // 无对比 → 原始布局
      currentEl ? (
        <div className="paramContent">
          <h4 className="taskParamTitle">输入参数</h4>
          <div className="paramGrid">{renderParamRows(currentEl, "elements")}</div>

          {currentEl.type === "ShellAndTubeExchanger" && currentEl.inputStreams?.length > 0 && (
            <>
              <h3 className="taskSectionTitle">入口流股</h3>
              <div className="paramGrid">
                {currentEl.inputStreams.map((s: any) => (
                  <div key={s.name} className="paramRow">
                    <span className="paramLabel">{s.name}</span>
                    <span className="paramValue">{s.streamAttribute}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {currentEl.type === "ShellAndTubeExchanger" && currentEl.outputStreams?.length > 0 && (
            <>
              <h3 className="taskSectionTitle">出口流股</h3>
              <div className="paramGrid">
                {currentEl.outputStreams.map((s: any) => (
                  <div key={s.name} className="paramRow">
                    <span className="paramLabel">{s.name}</span>
                    <span className="paramValue">{s.streamAttribute}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {currentEl.type !== "ShellAndTubeExchanger" && currentEl.type !== "DistilStrict" && currentEl.inputStreams?.length > 0 && (
            <>
              <h3 className="taskSectionTitle">入口流股</h3>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 30 }}>
                {currentEl.inputStreams.map((s: any) => (
                  <span key={s.name} className="paramLabel">{s.name}</span>
                ))}
              </div>
            </>
          )}

          {currentEl.type !== "ShellAndTubeExchanger" && currentEl.type !== "DistilStrict" && currentEl.outputStreams?.length > 0 && (
            <>
              <h3 className="taskSectionTitle">出口流股</h3>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 30 }}>
                {currentEl.outputStreams.map((s: any) => (
                  <span key={s.name} className="paramLabel">{s.name}</span>
                ))}
              </div>
            </>
          )}

          {currentEl.params?.output_f && currentEl.outputStreams?.length > 0 && (
            <>
              <h3 className="taskSectionTitle">输出流量</h3>
              <div className="paramGrid">
                {currentEl.outputStreams.map((s: any, idx: number) =>
                  currentEl.params.output_f[idx] !== undefined ? (
                    <div key={s.name} className="paramRow">
                      <span className="paramLabel">{s.name}</span>
                      <span className="paramValue">{currentEl.params.output_f[idx]}{currentEl.params.f_unit ? ` ${currentEl.params.f_unit}` : ""}</span>
                    </div>
                  ) : null
                )}
              </div>
            </>
          )}

          {currentEl.params?.Ns && currentEl.inputStreams && (
            <>
              <h3 className="taskSectionTitle">进料流股位置</h3>
              <div className="paramGrid">
                {renderFeedPositions(currentEl.params.Ns, currentEl.inputStreams)}
              </div>
            </>
          )}

          {currentEl.params?.No && currentEl.outputStreams && (
            <>
              <h3 className="taskSectionTitle">出料流股位置</h3>
              <div className="paramGrid">
                {renderProductPositions(currentEl.params.No, currentEl.outputStreams)}
              </div>
            </>
          )}

          {currentEl.params?.eff && currentEl.params.eff.length > 0 && (
            <>
              <h3 className="taskSectionTitle">塔板效率</h3>
              <table className="effTable">
                <thead>
                  <tr>
                    <th>起始塔板</th>
                    <th>结束塔板</th>
                    <th>塔板效率</th>
                    <th>塔板组分效率</th>
                  </tr>
                </thead>
                <tbody>
                  {currentEl.params.eff.map((e: any, idx: number) => (
                    <tr key={idx}>
                      <td>{e.start}</td>
                      <td>{e.end}</td>
                      <td>{e.value}</td>
                      <td>
                        {Array.isArray(e.spec) && e.spec.length > 0 ? (
                          <div style={{ display: "inline-flex", flexDirection: "column", gap: 4, textAlign: "left" }}>
                            {e.spec.map((sp: any, si: number) => {
                              const mat = materials?.[Number(sp?.[0]) - 1];
                              const matName = mat?.name || mat?.Name_ZH || mat?.chineseName || mat?.Molecular_formula || `组分${sp?.[0]}`;
                              return (
                                <span key={`spec-${si}`} style={{ margin: "2px 0"  }}>
                                  {matName}：{sp?.[1]}
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <span style={{ color: "#999" }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      ) : currentSt ? (
        <div className="paramContent">
          <h2 className="paramTitle">{currentSt.name} 参数</h2>
          <div className="paramGrid">{renderParamRows(currentSt, "streams")}</div>

          {currentSt.materials?.length > 0 && (
            <>
              <h2 className="paramTitle" style={{ marginTop: 20 }}>
                组分摩尔分率
              </h2>
              <div className="paramGrid">
                {renderComponentRows(currentSt.materials)}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="emptyTip"><Spin /></div>
      )}
    </div>
  );
};

export default ElementParamCompare;
